import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { MessageSquare, Zap } from 'lucide-react';
import { getArtStyle, DESK_ACCESSORIES, CHANNELS, CHARACTER_CATEGORIES } from './themes';

// Valid person emojis to ensure we show realistic avatars
const VALID_PERSON_EMOJIS = [
  '🧑', '👩', '👨', '🧑‍🦱', '👩‍🦰', '👨‍🦳', '👧', '👦',
  '👩‍💼', '👨‍💼', '👩‍💻', '👨‍💻', '👩‍🔬', '👨‍🏫', '👩‍🎨', '🧑‍⚕️',
  '🧙', '🧚', '🧛', '🧞', '🧜‍♀️', '🦸', '🧝', '🥷',
  '👨‍🚀', '👩‍🚀', '👽', '👾',
  '😊', '😎', '🤓', '😉', '🤩', '🤔', '😇', '🥳',
  '🤖', '🐱', '🐶', '🦊', '🦉', '🐼', '🐰', '🐻', '🐨',
];

// Get a valid emoji, falling back to a human emoji if invalid
const getValidEmoji = (emoji, agentName) => {
  // If emoji is valid, use it
  if (emoji && VALID_PERSON_EMOJIS.includes(emoji)) {
    return emoji;
  }

  // Check all categories for this emoji
  for (const category of Object.values(CHARACTER_CATEGORIES)) {
    const char = category.characters.find(c => c.emoji === emoji);
    if (char) return emoji;
  }

  // Fallback: generate a consistent person emoji based on agent name
  const personEmojis = ['👩‍💼', '👨‍💼', '👩‍💻', '👨‍💻', '🧑', '👩', '👨'];
  if (agentName) {
    const hash = agentName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return personEmojis[hash % personEmojis.length];
  }

  return '👩‍💼'; // Default professional female
};

// Channel colors matching Claw9Chat
const CHANNEL_COLORS = {
  zalo: { bg: 'bg-blue-500', color: '#0068ff' },
  telegram: { bg: 'bg-sky-500', color: '#0088cc' },
  facebook: { bg: 'bg-indigo-500', color: '#1877f2' },
  whatsapp: { bg: 'bg-emerald-500', color: '#25d366' },
  all: { bg: 'bg-slate-500', color: '#64748b' },
};

// Idle animation options
const IDLE_ANIMATIONS = [
  { id: 'typing', name: 'Typing', icon: '⌨️' },
  { id: 'drinking', name: 'Coffee Break', icon: '☕' },
  { id: 'thinking', name: 'Thinking', icon: '💭' },
  { id: 'reading', name: 'Reading', icon: '📖' },
  { id: 'idle', name: 'Idle', icon: '😊' },
];

/**
 * Get random accessories for the desk
 */
const getRandomAccessories = (seed, count = 2) => {
  const allAccessories = Object.entries(DESK_ACCESSORIES);
  const shuffled = [...allAccessories].sort(() => {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x) - 0.5;
  });
  return shuffled.slice(0, count).map(([key, val]) => ({ id: key, ...val }));
};

/**
 * Get style-specific classes
 */
const getStyleClasses = (styleId, isActive, agentColor) => {
  const styles = {
    modern: {
      container: `bg-gradient-to-b from-[#0d1424] to-[#0a0f1c] rounded-2xl border border-white/10
        ${isActive ? 'shadow-[0_0_40px_rgba(16,185,129,0.5)]' : 'shadow-[0_0_20px_rgba(255,77,77,0.2)]'}`,
      avatar: `rounded-2xl ${isActive
        ? 'bg-gradient-to-br from-emerald-500 to-emerald-400'
        : 'bg-gradient-to-br from-[#ff4d4d] to-[#ff6b6b]'}`,
      desk: 'bg-gradient-to-r from-slate-700 to-slate-600 rounded-xl',
      text: 'text-white',
      subtext: 'text-white/60',
    },
    pixel: {
      container: `bg-[#2d1b69] rounded-none border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]
        ${isActive ? 'border-[#00ff00]' : ''}`,
      avatar: `rounded-none border-4 border-black bg-[#4a3c8c] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]
        [image-rendering:pixelated] ${isActive ? 'bg-[#00aa00]' : ''}`,
      desk: 'bg-[#1a1a2e] border-2 border-black rounded-none',
      text: 'text-white',
      subtext: 'text-white/60',
      pixelated: true,
    },
    chibi: {
      container: `bg-gradient-to-b from-pink-200/30 to-purple-300/30 rounded-3xl border-4 border-white/40
        ${isActive ? 'shadow-[0_0_30px_rgba(236,72,153,0.5)]' : 'shadow-xl'}`,
      avatar: `rounded-full border-4 border-white bg-gradient-to-br from-pink-400 to-purple-500 shadow-xl`,
      desk: 'bg-white/40 backdrop-blur rounded-2xl',
      text: 'text-white',
      subtext: 'text-white/70',
      oversized: true,
    },
    roblox: {
      container: `bg-gradient-to-b from-[#00a2ff] to-[#0066cc] rounded-xl border-4 border-black/20
        shadow-[0_6px_0px_0px_rgba(0,0,0,0.3)] ${isActive ? 'translate-y-1 shadow-[0_2px_0px_0px_rgba(0,0,0,0.3)]' : ''}`,
      avatar: `rounded-lg border-4 border-black/30 bg-[#f5d742] shadow-[0_3px_0px_0px_rgba(0,0,0,0.3)]`,
      desk: 'bg-[#0066cc] border-b-4 border-black/30 rounded-lg',
      text: 'text-white',
      subtext: 'text-white/70',
    },
    minecraft: {
      container: `bg-[#8B8B8B] rounded-none border-4 border-[#555]
        shadow-[inset_-3px_-3px_0px_0px_rgba(0,0,0,0.4),inset_3px_3px_0px_0px_rgba(255,255,255,0.2)]
        ${isActive ? 'bg-[#5d8731]' : ''}`,
      avatar: `rounded-none border-4 border-[#3d3d3d] bg-[#c69c6d]
        shadow-[inset_-2px_-2px_0px_0px_rgba(0,0,0,0.4)] [image-rendering:pixelated]`,
      desk: 'bg-[#866043] border-4 border-[#3d3d3d] rounded-none',
      text: 'text-white',
      subtext: 'text-white/70',
      pixelated: true,
    },
    neon: {
      container: `bg-black/90 rounded-xl border-2
        ${isActive
          ? 'border-[#00ff88] shadow-[0_0_30px_#00ff88,inset_0_0_30px_rgba(0,255,136,0.1)]'
          : `border-[#ff4d4d] shadow-[0_0_20px_rgba(255,77,77,0.5)]`}`,
      avatar: `rounded-xl border-2 bg-black/50
        ${isActive
          ? 'border-[#00ff88] shadow-[0_0_20px_#00ff88]'
          : 'border-[#ff4d4d] shadow-[0_0_15px_rgba(255,77,77,0.5)]'}`,
      desk: 'bg-black/50 border-t-2 border-[#ff4d4d] rounded-xl',
      text: 'text-white',
      subtext: 'text-[#ff4d4d]',
      glow: true,
    },
    kawaii: {
      container: `bg-gradient-to-b from-[#fff5f5] to-[#ffe4e6] rounded-[1.5rem] border-4 border-white
        shadow-xl ${isActive ? 'animate-bounce-subtle' : ''}`,
      avatar: `rounded-full border-4 border-white bg-gradient-to-br from-[#fda4af] to-[#fb7185] shadow-lg`,
      desk: 'bg-white/70 rounded-2xl',
      text: 'text-rose-900',
      subtext: 'text-rose-700',
      bouncy: true,
    },
    minimal: {
      container: `bg-white rounded-2xl border border-gray-200 shadow-sm
        ${isActive ? 'ring-2 ring-emerald-500' : ''}`,
      avatar: 'rounded-full border-2 border-gray-200 bg-gray-100',
      desk: 'bg-gray-100 border border-gray-200 rounded-lg',
      text: 'text-gray-800',
      subtext: 'text-gray-500',
    },
  };

  return styles[styleId] || styles.modern;
};

/**
 * AgentDesk - Visual representation of an AI agent at their desk
 */
const AgentDesk = ({
  agent,
  activeEvent,
  artStyle = 'modern',
  size = 'medium',
  showAccessories = true,
  onClick,
}) => {
  const isActive = !!activeEvent;
  const customerChannel = activeEvent?.customer?.channel;
  const style = typeof artStyle === 'string' ? getArtStyle(artStyle) : artStyle;
  const styleId = style?.id || artStyle || 'modern';
  const classes = getStyleClasses(styleId, isActive, agent.color);

  // Random idle animation
  const [idleAnimation, setIdleAnimation] = useState('idle');

  useEffect(() => {
    if (isActive) return;
    const changeAnimation = () => {
      const newAnim = IDLE_ANIMATIONS[Math.floor(Math.random() * IDLE_ANIMATIONS.length)];
      setIdleAnimation(newAnim.id);
    };
    const interval = setInterval(changeAnimation, 5000 + Math.random() * 10000);
    return () => clearInterval(interval);
  }, [isActive]);

  // Consistent accessories per agent
  const accessories = useMemo(() => {
    if (!showAccessories) return [];
    const seed = agent.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return getRandomAccessories(seed, 2);
  }, [agent.id, showAccessories]);

  // Activity icon
  const activityIcon = isActive ? '💬' : IDLE_ANIMATIONS.find(a => a.id === idleAnimation)?.icon || '😊';

  // Size configs - FIXED HEIGHTS
  const sizeConfig = {
    small: { container: 'w-28', avatar: 'w-10 h-10', emoji: 'text-xl', text: 'text-[10px]' },
    medium: { container: 'w-36', avatar: 'w-14 h-14', emoji: 'text-2xl', text: 'text-xs' },
    large: { container: 'w-44', avatar: 'w-16 h-16', emoji: 'text-3xl', text: 'text-sm' },
  };
  const sizes = sizeConfig[size];

  // Idle animation style
  const getIdleStyle = () => {
    if (isActive) return {};
    const anims = {
      typing: { animation: 'agent-typing 0.8s ease-in-out infinite' },
      drinking: { animation: 'agent-drinking 3s ease-in-out infinite' },
      thinking: { animation: 'agent-thinking 2s ease-in-out infinite' },
      reading: { animation: 'agent-reading 2s ease-in-out infinite' },
      idle: { animation: 'agent-idle 3s ease-in-out infinite' },
    };
    return anims[idleAnimation] || anims.idle;
  };

  return (
    <div
      onClick={onClick}
      className={`
        ${sizes.container} ${classes.container}
        p-3 flex flex-col items-center gap-2
        transition-all duration-300 cursor-pointer hover:scale-105 hover:z-10 relative
      `}
    >
      {/* Pulse effect when active */}
      {isActive && !classes.pixelated && (
        <div className="absolute inset-0 rounded-2xl animate-ping opacity-20 bg-emerald-500 pointer-events-none" />
      )}

      {/* Scanlines for pixel styles */}
      {classes.pixelated && (
        <div className="absolute inset-0 pointer-events-none opacity-10"
          style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)' }}
        />
      )}

      {/* Top row: assigned channels + status */}
      <div className="w-full flex justify-between items-center px-1">
        {/* Assigned channel badges */}
        <div className="flex gap-0.5">
          {(agent.channels || ['all']).slice(0, 3).map((ch) => {
            const channelConfig = CHANNEL_COLORS[ch] || CHANNEL_COLORS.all;
            const channelInfo = CHANNELS[ch];
            return (
              <div
                key={ch}
                className={`w-4 h-4 ${classes.pixelated ? 'rounded-none' : 'rounded-full'}
                  ${isActive && customerChannel === ch ? 'animate-pulse ring-2 ring-white' : ''}
                  flex items-center justify-center text-[8px]`}
                style={{ backgroundColor: channelConfig.color }}
                title={channelInfo?.name || ch}
              >
                {ch === 'all' ? '📱' : channelInfo?.icon || ch[0].toUpperCase()}
              </div>
            );
          })}
        </div>

        {/* Activity + status */}
        <div className="flex items-center gap-1">
          <span className="text-[10px]">{activityIcon}</span>
          <span className={`w-2 h-2 ${classes.pixelated ? 'rounded-none' : 'rounded-full'}
            ${isActive ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'}`}
          />
        </div>
      </div>

      {/* Avatar */}
      <div className="relative">
        <div
          className={`${sizes.avatar} ${classes.avatar} flex items-center justify-center transition-all duration-300
            ${isActive ? 'scale-110' : ''} ${classes.oversized ? 'scale-125' : ''}`}
          style={getIdleStyle()}
        >
          {agent.avatar ? (
            <img src={agent.avatar} alt={agent.name}
              className={`w-full h-full object-cover ${classes.pixelated ? '[image-rendering:pixelated]' : ''}`}
              style={{ borderRadius: 'inherit' }}
            />
          ) : (
            <span className={sizes.emoji}>{getValidEmoji(agent.emoji || agent.icon, agent.name)}</span>
          )}
        </div>

        {/* Typing dots when active */}
        {isActive && (
          <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-0.5 px-1.5 py-0.5
            ${classes.pixelated ? 'bg-black rounded-none' : 'bg-black/60 rounded-full'}`}>
            {[0, 1, 2].map((i) => (
              <span key={i} className={`w-1 h-1 bg-emerald-400 ${classes.pixelated ? 'rounded-none' : 'rounded-full'} animate-bounce`}
                style={{ animationDelay: `${i * 150}ms` }} />
            ))}
          </div>
        )}
      </div>

      {/* Customer name bubble when active */}
      {isActive && activeEvent?.customer && (
        <div className={`absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap
          px-2 py-0.5 text-[9px] bg-white text-gray-800 shadow-lg z-20
          ${classes.pixelated ? 'rounded-none' : 'rounded-lg'}`}>
          {activeEvent.customer.name}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white transform rotate-45" />
        </div>
      )}

      {/* Name */}
      <p className={`${sizes.text} font-semibold ${classes.text} truncate max-w-full text-center`}>
        {agent.name}
      </p>

      {/* Desk with accessories */}
      <div className={`w-full ${classes.desk} px-2 py-1.5 relative`}>
        {/* Accessories */}
        {showAccessories && accessories.length > 0 && (
          <div className="absolute -top-2 left-0 right-0 flex justify-between px-1">
            {accessories.map((acc, i) => (
              <span key={acc.id} className="text-[10px] animate-float-slow" style={{ animationDelay: `${i * 0.5}s` }}>
                {acc.emoji}
              </span>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center justify-center gap-2 mt-1">
          <div className={`flex items-center gap-0.5 ${classes.subtext}`}>
            <MessageSquare className="w-3 h-3" />
            <span className={sizes.text}>{agent.activeChats || 0}</span>
          </div>
          <div className={`flex items-center gap-0.5 ${classes.subtext}`}>
            <Zap className="w-3 h-3" />
            <span className={sizes.text}>{agent.responsesCount || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

AgentDesk.propTypes = {
  agent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    avatar: PropTypes.string,
    emoji: PropTypes.string,
    icon: PropTypes.string,
    color: PropTypes.string,
    activeChats: PropTypes.number,
    responsesCount: PropTypes.number,
  }).isRequired,
  activeEvent: PropTypes.object,
  artStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  showAccessories: PropTypes.bool,
  onClick: PropTypes.func,
};

export default AgentDesk;
